import { SelectOption } from '@common/types/common';
import { CompanyManagementContext } from '@company/contexts/CompanyManagementContext';
import { useCreatePartnershipRequestMutation, useGetAvailableCompaniesToPartnerQuery } from '@company/services/company';
import { CloseButton } from '@components/utility/buttons';
import {
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    TextField,
} from '@mui/material';
import { handleError, toaster } from '@utils/common';
import { SyntheticEvent, useCallback, useContext, useState } from 'react';

const InviteCompanyDialog = (props: DialogProps) => {
    const [selectedCompanies, setSelectedCompanies] = useState<SelectOption<string>[]>([]);
    const [isCreateCompanyRequestLoading, setIsCreateCompanyRequestLoading] = useState(false);
    const { company } = useContext(CompanyManagementContext);
    const { data: companiesAvailableToInvite, isLoading } = useGetAvailableCompaniesToPartnerQuery(
        { company_id: company?.id ?? '' },
        {
            skip: !company?.id || !props.open,
            selectFromResult: (response) => ({
                ...response,
                data: response.data?.map((availableCompany) => ({
                    label: availableCompany.name,
                    value: availableCompany.id,
                })),
            }),
        }
    );

    const [createCompanyRequest] = useCreatePartnershipRequestMutation();

    const handleChange = useCallback(
        (_event: SyntheticEvent<Element, Event>, value: SelectOption<string>[]) => setSelectedCompanies(value),
        []
    );

    const handleInvite = async () => {
        try {
            setIsCreateCompanyRequestLoading(true);
            if (company?.id) {
                const errors: string[] = [];
                const promises = selectedCompanies.map(async (selectedCompany) => {
                    try {
                        await createCompanyRequest({
                            company_id: company?.id,
                            to_companies_ids: [selectedCompany.value],
                        }).unwrap();
                    } catch (error) {
                        errors.push(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            `${selectedCompany.label}: ${(error as any)?.data?.[0]}`
                        );
                    }
                });
                await Promise.all(promises);
                if (errors.length) {
                    throw { errors };
                }
                props.onClose?.({}, 'escapeKeyDown');
                toaster.success({ msg: 'Successfully invited companies' });
                setSelectedCompanies([]);
            }
        } catch (err) {
            handleError('Error inviting companies', err);
        } finally {
            setIsCreateCompanyRequestLoading(false);
        }
    };

    return (
        <Dialog
            {...props}
            slotProps={{
                paper: { className: 'sm:!w-[800px] sm:!min-w-0 md:gap-1 ' + props.PaperProps?.className },
            }}
        >
            <CloseButton onClick={() => props.onClose?.({}, 'escapeKeyDown')} />
            <DialogTitle className="!font-museo !font-bold md:!text-4xl text-center !mx-10 sm:!mx-20">
                Send partnership invitations to companies
            </DialogTitle>
            <DialogContent>
                <Autocomplete
                    id="available-companies"
                    multiple
                    options={companiesAvailableToInvite ?? []}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            label="Companies"
                            placeholder="Search companies to partner with"
                        />
                    )}
                    noOptionsText="No companies"
                    filterSelectedOptions
                    onChange={handleChange}
                    value={selectedCompanies}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    loading={isLoading}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    disabled={isCreateCompanyRequestLoading || !selectedCompanies.length}
                    className="!mx-auto !px-10 !py-2"
                    onClick={handleInvite}
                    startIcon={isCreateCompanyRequestLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    <span>Send</span>
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { InviteCompanyDialog };
